/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button";
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Select from '@mui/material/Select';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../shared-components/forms/NumberedHeading";
import EventArray from "./EventArray"

export default function NavigationArray({ control, register, setValue, getValues, errors, inputDir, pageList }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'navigation',
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion defaultExpanded className="bg-transparent shadow-none">
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold" sx={{lineHeight: '28px'}}>
                      Subnav {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >

                      <Controller
                        control={control}
                        name={`navigation.${index}.id`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`navigation.${index}.review.status`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`navigation.${index}.review.approved`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`navigation.${index}.review.approval_date`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <NumberedHeading number="1" title="image" instructions='Insert image name' />
                      <Controller
                        control={control}
                        name={`navigation.${index}.image`}
                        render={({ field }) => (
                            <TextField
                              {...field}
                              label="Image"
                              placeholder="Image"
                              id={`navigation.${index}.image`}
                              error={!!errors.navigation?.[index]?.image}
                              helperText={errors?.navigation?.[index]?.image?.message}
                              variant="outlined"
                              required
                              fullWidth
                              size="small"
                            />
                        )}
                      />

                      <NumberedHeading number="2" title="Events" instructions="Add Events" />
                      <Controller
                        control={control}
                        name={`navigation.${index}.events`}
                        render={({field}) => (
                            <EventArray
                              nestIndex={index}
                              inputDir={inputDir}
                              {...{control, errors}}
                              pageList={pageList}
                            />
                        )}
                      />

                      {/* <NumberedHeading number="1" title="Page of Event" instructions="Page ID is required" required />
                      <Controller
                        control={control}
                        name={`navigation.${index}.page_id`}
                        render={({ field }) => (
                            <FormControl fullWidth className='mt-16' size="small" error={!!errors.navigation?.[index]?.page_id}>
                                <InputLabel id="select-label">Page of Event</InputLabel>
                                <Select
                                  {...field}
                                  labelId="select-tag"
                                  id="page_id"
                                  label="Page of Event"
                                  classes={{ select: "flex items-center space-x-12" }}
                                  defaultValue=""
                                >
                                  <MenuItem value="" className="space-x-12" disabled>
                                    Select a Page of Event
                                  </MenuItem>
                                  {pageList.map((item, i) => (
                                    <MenuItem key={`navigation.${index}.page_id.${i}`} value={pageList[i].id} className="space-x-12">
                                      {pageList[i].title}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {!!errors.navigation?.[index]?.page_id ? (
                                    <FormHelperText>{errors.navigation?.[index]?.page_id?.message}</FormHelperText>
                                ) : null}
                            </FormControl>
                        )}
                      />

                      <NumberedHeading number="2" title="Column" instructions='Insert Column #' />
                      <Controller
                        control={control}
                        name={`navigation.${index}.column`}
                        render={({ field }) => (
                            <TextField
                              {...field}
                              label="column"
                              placeholder="Column"
                              id={`navigation.${index}.column`}
                              error={!!errors.navigation?.[index]?.column}
                              helperText={errors?.navigation?.[index]?.column?.message}
                              variant="outlined"
                              required
                              fullWidth
                              size="small"
                            />
                        )}
                      />

                      <NumberedHeading number="3" title="Row" instructions='Insert Row #' />
                      <Controller
                        control={control}
                        name={`navigation.${index}.row`}
                        render={({ field }) => (
                            <TextField
                              {...field}
                              label="row"
                              placeholder="Row"
                              id={`navigation.${index}.row`}
                              error={!!errors.navigation?.[index]?.row}
                              helperText={errors?.navigation?.[index]?.row?.message}
                              variant="outlined"
                              required
                              fullWidth
                              size="small"
                            />
                        )}
                      />

                      <NumberedHeading number="4" title="Type" instructions='Insert Type' />
                      <Controller
                        control={control}
                        name={`navigation.${index}.type`}
                        render={({ field }) => (
                            <TextField
                              {...field}
                              label="type"
                              placeholder="Type"
                              id={`navigation.${index}.type`}
                              error={!!errors.navigation?.[index]?.type}
                              helperText={errors?.navigation?.[index]?.type?.message}
                              variant="outlined"
                              required
                              fullWidth
                              size="small"
                            />
                        )}
                      /> */}
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            id: BASEUtils.generateGUID(),
            image: "",
            events: [],
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Subnav
          </span>
        </Button>
      </Box>
    </>
  );
}
